const API_URL = import.meta.env.VITE_API_URL || 'https://zen-dashboard-api.teleclaw.workers.dev'

function getInitData(): string {
  try {
    return window.Telegram?.WebApp?.initData || ''
  } catch {
    return ''
  }
}

async function fetchAPI<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Init-Data': getInitData(),
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `HTTP ${res.status}`)
  }
  return res.json()
}

// Types
export interface Project {
  id: string
  name: string
  emoji: string
  description: string
  status: string
  url: string
  sort_order: number
  created_at: string
  updated_at: string
  open_tasks: number
  done_tasks: number
}

export interface Task {
  id: string
  title: string
  description: string | null
  priority: 'urgent' | 'this_week' | 'backlog' | 'normal'
  completed: number
  project_id: string | null
  category: 'work' | 'personal'
  assigned_to: string | null
  time_estimate: string | null
  due_date: string | null
  created_at: string
  updated_at: string
  completed_at: string | null
  project_name: string | null
  project_emoji: string | null
  subtask_total?: number
  subtask_completed?: number
}

export interface Subtask {
  id: string
  task_id: string
  title: string
  completed: number
  sort_order: number
  created_at: string
  completed_at: string | null
}

export interface TeamMember {
  id: string
  name: string
  role: string | null
  emoji: string
}

export interface FocusTask extends Task {
  subtasks?: Subtask[]
}

export interface DailySummary {
  completedToday: number
  urgentTotal: number
  teamCheckIns: Array<{
    assigned_to: string
    task_count: number
    earliest_due: string
  }>
}

export interface Deadline {
  id: string
  name: string
  emoji: string
  target_date: string
  description: string
  color: string
  created_at: string
}

export interface Stats {
  activeProjects: number
  openTasks: number
  completedTasks: number
  nextDeadline: string | null
}

// API functions
export const api = {
  getStats: () => fetchAPI<Stats>('/api/stats'),
  getProjects: () => fetchAPI<Project[]>('/api/projects'),
  getTasks: (options?: { 
    status?: string, 
    category?: 'work' | 'personal', 
    assigned_to?: string,
    daily_focus?: 'today'
  }) => {
    const params = new URLSearchParams()
    if (options?.status) params.append('status', options.status)
    if (options?.category) params.append('category', options.category)
    if (options?.assigned_to) params.append('assigned_to', options.assigned_to)
    if (options?.daily_focus) params.append('daily_focus', options.daily_focus)
    const query = params.toString()
    return fetchAPI<Task[]>(`/api/tasks${query ? `?${query}` : ''}`)
  },
  getDeadlines: () => fetchAPI<Deadline[]>('/api/deadlines'),
  getTeamMembers: () => fetchAPI<TeamMember[]>('/api/team'),
  getFocusTask: (category?: 'work' | 'personal', exclude?: string[]) => {
    const params = new URLSearchParams()
    if (category) params.append('category', category)
    if (exclude?.length) params.append('exclude', exclude.join(','))
    const query = params.toString()
    return fetchAPI<FocusTask | null>(`/api/focus${query ? `?${query}` : ''}`)
  },
  getDailySummary: (category?: 'work' | 'personal') =>
    fetchAPI<DailySummary>(`/api/daily-summary${category ? `?category=${category}` : ''}`),
  
  createTask: (data: { 
    title: string; 
    description?: string; 
    priority?: string; 
    project_id?: string;
    category?: 'work' | 'personal';
    assigned_to?: string;
    time_estimate?: string;
    due_date?: string;
  }) =>
    fetchAPI<Task>('/api/tasks', { method: 'POST', body: JSON.stringify(data) }),
  
  updateTask: (id: string, data: { 
    completed?: boolean; 
    title?: string; 
    priority?: string;
    category?: 'work' | 'personal';
    assigned_to?: string;
    time_estimate?: string;
    due_date?: string;
    daily_focus_date?: string | null;
  }) =>
    fetchAPI<Task>(`/api/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  
  deleteTask: (id: string) =>
    fetchAPI<{ ok: boolean }>(`/api/tasks/${id}`, { method: 'DELETE' }),
  
  // Subtask functions
  getSubtasks: (taskId: string) => 
    fetchAPI<Subtask[]>(`/api/tasks/${taskId}/subtasks`),
  
  createSubtask: (taskId: string, data: { title: string; sort_order?: number }) =>
    fetchAPI<Subtask>(`/api/tasks/${taskId}/subtasks`, { 
      method: 'POST', 
      body: JSON.stringify(data) 
    }),
  
  updateSubtask: (id: string, data: { completed?: boolean; title?: string; sort_order?: number }) =>
    fetchAPI<Subtask>(`/api/subtasks/${id}`, { 
      method: 'PATCH', 
      body: JSON.stringify(data) 
    }),
  
  deleteSubtask: (id: string) =>
    fetchAPI<{ ok: boolean }>(`/api/subtasks/${id}`, { method: 'DELETE' }),

  // Generic get
  get: <T = any>(path: string) => fetchAPI<T>(path),
}
